import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  ColorType,
  createChart,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp
} from "lightweight-charts";
import { Activity, AlertTriangle, ArrowUpRight, Brush, Camera, Crosshair, Database, Eraser, FolderPlus, LayoutTemplate, Magnet, Maximize2, Minus, PencilLine, RectangleHorizontal, RefreshCw, Ruler, Save, TrendingUp, Trash2, Undo2, WifiOff } from "lucide-react";
import { analyzeIndicators, ema, sma } from "./lib/indicators";
import {
  candleStats,
  chartHandleScaleOptions,
  chartHandleScrollOptions,
  chartDrawingStorageKey,
  chartTypes,
  defaultIndicators,
  drawingToolNeedsEnd,
  formatPrice,
  heikinAshi,
  initialLimitForTimeframe,
  intervalFromTimeframe,
  isPlausiblePriceLine,
  liveUpdateCandles,
  mainPriceScaleOptions,
  mainPricePaneIndex,
  normalizeChartCandles,
  normalizeChartAnnotations,
  pollingMsForTimeframe,
  chartPricePrecision,
  shouldFitChart,
  seriesDiagnostic,
  timeframeOptions,
  toChartTime,
  toCloseSeries,
  toOhlcSeries,
  toVolumeSeries,
  type CandleUpdateMode,
  type ChartAnnotation,
  type ChartDrawingTool,
  volumePaneIndex,
  type ChartType,
  type TimeframeLabel
} from "./lib/charting";
import type { Candle, CandleResult, FinalDirection, MarketScan, TradingSignal } from "./lib/types";
import "./styles.css";

interface ApiStatus {
  ok: boolean;
  marketData?: { order: string[]; providers: Array<{ provider: string; status: string; errorCount: number; lastError: string | null; reason?: string | null; disabledUntil?: string | null }> };
  generatedAt: string;
}

type LoadState = "loading" | "success" | "empty" | "degraded" | "error" | "offline";
type ChartContextKind = "chart" | "candle" | "signal-line" | "price-axis" | "time-axis";
type ChartContextMenu = { open: boolean; x: number; y: number; touch: boolean; kind: ChartContextKind; candle: Candle | null; price: number | null };

const storageKey = "youdinix-terminal-v2";

interface SavedChartLayout {
  chartType: ChartType;
  indicators: string[];
  grid: boolean;
  crosshair: boolean;
  magnet: boolean;
}

interface TerminalSettings {
  symbol?: string;
  timeframe?: TimeframeLabel;
  chartType?: ChartType;
  indicators?: string[];
  autoRefresh?: boolean;
  grid?: boolean;
  crosshair?: boolean;
  magnet?: boolean;
  chartLayouts?: Record<string, SavedChartLayout>;
  chartDrawings?: Record<string, ChartAnnotation[]>;
  watchlists?: Record<string, string[]>;
  activeWatchlist?: string;
}

const builtInChartLayouts: Record<string, SavedChartLayout> = {
  Trend: { chartType: "Candles", indicators: ["volume", "ema20", "ema50", "ema200"], grid: true, crosshair: true, magnet: false },
  Momentum: { chartType: "Candles", indicators: ["volume", "ema20", "ema50"], grid: true, crosshair: true, magnet: false },
  Clean: { chartType: "Candles", indicators: [], grid: false, crosshair: true, magnet: false }
};

const builtInWatchlists: Record<string, string[]> = {
  Core: ["BTCUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT"],
  Layer1: ["SOLUSDT", "AVAXUSDT", "SUIUSDT", "APTUSDT"]
};

function formatTime(value: string | number | null | undefined): string {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" }).format(new Date(value));
}

function readSettings() {
  try {
    return JSON.parse(window.localStorage.getItem(storageKey) || "{}") as TerminalSettings;
  } catch {
    return {};
  }
}

function saveSettings(settings: Record<string, unknown>) {
  const current = readSettings();
  window.localStorage.setItem(storageKey, JSON.stringify({ ...current, ...settings }));
}

function compactErrorText(message: string | null | undefined): string {
  if (!message) return "";
  return message
    .replace(/<[^>]*>/g, " ")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s+/g, " ")
    .replace(/No market data provider returned valid candles for ([A-Z0-9]+) ([^:]+):/g, "$1 $2:")
    .trim()
    .slice(0, 260);
}

function providerSummary(status: ApiStatus | null, error: string | null) {
  const providers = status?.marketData?.providers || [];
  const affected = providers.filter((provider) => provider.status !== "available" || provider.errorCount > 0);
  const fallback = providers.find((provider) => provider.status === "available")?.provider || status?.marketData?.order?.[0] || "-";
  return {
    affectedCount: affected.length,
    fallback,
    lines: affected.length
      ? affected.map((provider) => `${provider.provider}: ${provider.reason || compactErrorText(provider.lastError) || provider.status}`)
      : compactErrorText(error) ? [compactErrorText(error)] : []
  };
}

function normalizeAnalysisDecision(signal: TradingSignal | null | undefined): { finalDirection: FinalDirection; label: string; className: string; qualified: boolean } {
  if (!signal) return { finalDirection: "NO_DATA", label: "NO DATA", className: "wait", qualified: false };
  if (signal.finalDirection) {
    const finalDirection = signal.finalDirection;
    const className = finalDirection === "LONG" || finalDirection === "WATCH_LONG" ? "long" : finalDirection === "SHORT" || finalDirection === "WATCH_SHORT" ? "short" : "wait";
    return { finalDirection, label: finalDirection.replace("_", " "), className, qualified: Boolean(signal.qualified) };
  }
  if (signal.dataQuality === "LOW") return { finalDirection: "NO_DATA", label: "NO DATA", className: "wait", qualified: false };
  if (signal.spotDecision === "AVOID" || signal.spotDecision === "RISK-OFF" || signal.grade === "AVOID") return { finalDirection: "AVOID", label: "AVOID", className: "wait", qualified: false };
  const qualified = signal.direction !== "WAIT" && ["A+", "A"].includes(signal.grade) && signal.confidence >= 80;
  if (qualified && signal.direction === "LONG") return { finalDirection: "LONG", label: "LONG", className: "long", qualified: true };
  if (qualified && signal.direction === "SHORT") return { finalDirection: "SHORT", label: "SHORT", className: "short", qualified: true };
  if (signal.direction === "LONG" || signal.indicators.trend === "bullish") return { finalDirection: "WATCH_LONG", label: "WATCH LONG", className: "long", qualified: false };
  if (signal.direction === "SHORT" || signal.indicators.trend === "bearish") return { finalDirection: "WATCH_SHORT", label: "WATCH SHORT", className: "short", qualified: false };
  return { finalDirection: "WAIT", label: "WAIT", className: "wait", qualified: false };
}

function useMarketDesk(autoRefresh: boolean) {
  const [scan, setScan] = useState<MarketScan | null>(null);
  const [status, setStatus] = useState<ApiStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>("loading");
  const [loading, setLoading] = useState(false);
  const [lastSuccess, setLastSuccess] = useState<number | null>(null);
  const [nextUpdate, setNextUpdate] = useState<number | null>(null);
  const [backoff, setBackoff] = useState(1);
  const inFlight = useRef<AbortController | null>(null);
  const hasScanRef = useRef(false);

  const load = useCallback(async () => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setLoading(true);
    try {
      const [scanResponse, statusResponse] = await Promise.all([
        fetch("/api/analyze", { cache: "no-store", signal: controller.signal }),
        fetch("/api/status", { cache: "no-store", signal: controller.signal })
      ]);
      const statusPayload = await statusResponse.json() as ApiStatus;
      setStatus(statusPayload);
      const scanPayload = await scanResponse.json() as MarketScan | { error?: string };
      if (!scanResponse.ok) throw new Error("error" in scanPayload && scanPayload.error ? scanPayload.error : "Analysis failed");
      const nextScan = scanPayload as MarketScan;
      setScan(nextScan);
      hasScanRef.current = true;
      setError(null);
      setBackoff(1);
      setLastSuccess(Date.now());
      setState(nextScan.opportunities.length ? statusPayload.ok ? "success" : "degraded" : "empty");
    } catch (loadError) {
      if (loadError instanceof DOMException && loadError.name === "AbortError") return;
      setError(compactErrorText(loadError instanceof Error ? loadError.message : "Analysis failed"));
      setBackoff((value) => Math.min(value * 2, 8));
      setState(navigator.onLine ? hasScanRef.current ? "degraded" : "error" : "offline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    if (!autoRefresh) return undefined;
    let timer = 0;
    const schedule = () => {
      const jitter = Math.floor(Math.random() * 5000);
      const delay = document.hidden ? 180000 : 45000 * backoff + jitter;
      setNextUpdate(Date.now() + delay);
      timer = window.setTimeout(async () => {
        if (!document.hidden) await load();
        schedule();
      }, delay);
    };
    schedule();
    const sync = () => {
      window.clearTimeout(timer);
      if (!document.hidden) void load().finally(schedule);
    };
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [autoRefresh, backoff, load]);

  return { scan, status, error, state, loading, lastSuccess, nextUpdate, stale: !!error && !!scan, reload: load };
}

function useCandles(symbol: string | null, timeframe: TimeframeLabel, autoRefresh: boolean) {
  const [result, setResult] = useState<CandleResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<number | null>(null);
  const [nextUpdate, setNextUpdate] = useState<number | null>(null);
  const [updateMode, setUpdateMode] = useState<CandleUpdateMode>("full");
  const [backoff, setBackoff] = useState(1);
  const inFlight = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async (mode: "full" | "tail" = "full") => {
    if (!symbol) return;
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setLoading(mode === "full");
    const limit = mode === "tail" ? 5 : initialLimitForTimeframe(timeframe);
    try {
      const response = await fetch(`/api/candles/${symbol}?interval=${encodeURIComponent(intervalFromTimeframe(timeframe))}&limit=${limit}`, {
        cache: "no-store",
        signal: controller.signal
      });
      const payload = await response.json() as CandleResult | { error?: string };
      if (!response.ok) throw new Error("error" in payload && payload.error ? compactErrorText(payload.error) : "Candle request failed");
      if (!mounted.current) return;
      setUpdateMode(mode);
      setResult((previous) => {
        const incoming = payload as CandleResult;
        return previous && mode === "tail"
          ? { ...incoming, candles: liveUpdateCandles(previous.candles, incoming.candles).candles }
          : incoming;
      });
      setError(null);
      setBackoff(1);
      setLastUpdate(Date.now());
    } catch (loadError) {
      if (loadError instanceof DOMException && loadError.name === "AbortError") return;
      setError(compactErrorText(loadError instanceof Error ? loadError.message : "Candle request failed"));
      setBackoff((value) => Math.min(value * 2, 8));
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe]);

  useEffect(() => {
    mounted.current = true;
    setResult(null);
    setError(null);
    void load("full");
    return () => {
      mounted.current = false;
      inFlight.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    if (!autoRefresh || !symbol) return undefined;
    let timer = 0;
    const schedule = () => {
      const delay = document.hidden ? pollingMsForTimeframe(timeframe) * 4 : pollingMsForTimeframe(timeframe) * backoff;
      setNextUpdate(Date.now() + delay);
      timer = window.setTimeout(async () => {
        if (navigator.onLine) await load("tail");
        schedule();
      }, delay);
    };
    schedule();
    const sync = () => {
      window.clearTimeout(timer);
      void load("tail").finally(schedule);
    };
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("online", sync);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("online", sync);
    };
  }, [autoRefresh, backoff, load, symbol, timeframe]);

  return { result, error, loading, lastUpdate, nextUpdate, updateMode, reload: () => load("full") };
}

function StatusDot({ state }: { state: LoadState }) {
  return <span className={`status-dot ${state}`} aria-label={state} />;
}

function TopBar({ scan, status, state, loading, autoRefresh, setAutoRefresh, lastUpdate, nextUpdate, onRefresh }: {
  scan: MarketScan | null;
  status: ApiStatus | null;
  state: LoadState;
  loading: boolean;
  autoRefresh: boolean;
  setAutoRefresh: (value: boolean) => void;
  lastUpdate: number | null;
  nextUpdate: number | null;
  onRefresh: () => void;
}) {
  const provider = scan?.highestProbabilityOpportunity?.dataProvider || status?.marketData?.order?.[0] || "-";
  return (
    <header className="terminal-topbar">
      <div className="brand-block"><strong>Youdinix AI</strong><span>Founder Yousif A.</span></div>
      <div className="topbar-grid">
        <span><StatusDot state={state} />{state}</span>
        <span>Last {formatTime(lastUpdate || scan?.generatedAt)}</span>
        <span>Next {formatTime(nextUpdate)}</span>
        <span><Database size={14} />Provider {provider}</span>
        <span>Regime {scan?.trend || "-"}</span>
      </div>
      <a className="toolbar-link" href="/admin">Admin</a>
      <button className={autoRefresh ? "toolbar-button active" : "toolbar-button"} onClick={() => setAutoRefresh(!autoRefresh)}>Auto Refresh {autoRefresh ? "ON" : "OFF"}</button>
      <button className="icon-button" onClick={onRefresh} disabled={loading} aria-label="Refresh market analysis" title="Refresh market analysis"><RefreshCw size={16} /></button>
    </header>
  );
}

function ErrorBanner({ status, error, onRetry }: { status: ApiStatus | null; error: string | null; onRetry: () => void }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const summary = providerSummary(status, error);
  if (dismissed || !error && summary.affectedCount === 0) return null;
  return (
    <section className="data-alert">
      <button className="data-alert-main" onClick={() => setOpen(!open)}>
        <AlertTriangle size={15} />
        <span>Market data degraded</span>
        <strong>{summary.affectedCount || 1} affected</strong>
        <small>Fallback {summary.fallback}</small>
      </button>
      <button className="toolbar-button" onClick={onRetry}>Retry</button>
      <button className="toolbar-button" onClick={() => setOpen(!open)}>{open ? "Hide" : "Details"}</button>
      <button className="toolbar-button" onClick={() => setDismissed(true)}>Dismiss</button>
      {open ? <div className="data-alert-details">{summary.lines.slice(0, 6).map((line) => <span key={line}>{line}</span>)}</div> : null}
    </section>
  );
}

function MarketWatch({ opportunities, activeSymbol, setActiveSymbol, stale }: { opportunities: TradingSignal[]; activeSymbol: string | null; setActiveSymbol: (symbol: string) => void; stale: boolean }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"ALL" | FinalDirection>("ALL");
  const [customWatchlists, setCustomWatchlists] = useState<Record<string, string[]>>(() => {
    const stored = readSettings().watchlists || {};
    return Object.fromEntries(Object.entries(stored)
      .filter(([name, symbols]) => name.trim().length > 0 && Array.isArray(symbols))
      .map(([name, symbols]) => [name.trim().slice(0, 36), [...new Set(symbols.map((symbol) => String(symbol).toUpperCase()).filter((symbol) => /^[A-Z0-9]{5,20}$/.test(symbol)))].slice(0, 40)]));
  });
  const [activeWatchlist, setActiveWatchlist] = useState(() => {
    const configured = readSettings().activeWatchlist || "Live";
    return configured === "Live" || Object.hasOwn(builtInWatchlists, configured) || Object.hasOwn(customWatchlists, configured)
      ? configured
      : "Live";
  });
  const watchlists = { ...builtInWatchlists, ...customWatchlists };
  const selectedSymbols = activeWatchlist === "Live" ? null : watchlists[activeWatchlist] || [];
  const rows = opportunities
    .filter((item) => !selectedSymbols || selectedSymbols.includes(item.symbol))
    .filter((item) => item.coin.toLowerCase().includes(query.toLowerCase()) || item.symbol.toLowerCase().includes(query.toLowerCase()))
    .filter((item) => filter === "ALL" || normalizeAnalysisDecision(item).finalDirection === filter)
    .sort((a, b) => b.confidence - a.confidence);

  const chooseWatchlist = (name: string) => {
    const next = name === "Live" || watchlists[name] ? name : "Live";
    setActiveWatchlist(next);
    saveSettings({ activeWatchlist: next });
  };

  const createWatchlist = () => {
    const requested = window.prompt("Name der neuen Watchlist", "Meine Watchlist")?.trim().slice(0, 36);
    if (!requested || Object.hasOwn(watchlists, requested)) return;
    const next = { ...customWatchlists, [requested]: activeSymbol ? [activeSymbol] : [] };
    setCustomWatchlists(next);
    setActiveWatchlist(requested);
    saveSettings({ watchlists: next, activeWatchlist: requested });
  };

  const addActiveSymbol = () => {
    if (!activeSymbol || activeWatchlist === "Live" || Object.hasOwn(builtInWatchlists, activeWatchlist)) return;
    const next = { ...customWatchlists, [activeWatchlist]: [...new Set([...(customWatchlists[activeWatchlist] || []), activeSymbol])] };
    setCustomWatchlists(next);
    saveSettings({ watchlists: next });
  };

  const deleteWatchlist = () => {
    if (activeWatchlist === "Live" || Object.hasOwn(builtInWatchlists, activeWatchlist)) return;
    if (!window.confirm(`Watchlist ${activeWatchlist} loeschen?`)) return;
    const next = { ...customWatchlists };
    delete next[activeWatchlist];
    setCustomWatchlists(next);
    chooseWatchlist("Live");
    saveSettings({ watchlists: next, activeWatchlist: "Live" });
  };

  return (
    <aside className="panel market-watch">
      <div className="panel-header"><span>Market Watch</span><div className="watchlist-controls"><select value={activeWatchlist} onChange={(event) => chooseWatchlist(event.target.value)} aria-label="Watchlist auswaehlen"><option value="Live">Live</option>{Object.keys(watchlists).sort((left, right) => left.localeCompare(right)).map((name) => <option key={name} value={name}>{name}</option>)}</select><button className="icon-button" onClick={createWatchlist} aria-label="Neue Watchlist" title="Neue Watchlist"><FolderPlus size={14} /></button>{!Object.hasOwn(builtInWatchlists, activeWatchlist) && activeWatchlist !== "Live" ? <button className="icon-button" onClick={addActiveSymbol} aria-label="Aktives Asset hinzufuegen" title="Aktives Asset hinzufuegen"><Save size={14} /></button> : null}{!Object.hasOwn(builtInWatchlists, activeWatchlist) && activeWatchlist !== "Live" ? <button className="icon-button" onClick={deleteWatchlist} aria-label="Watchlist loeschen" title="Watchlist loeschen"><Trash2 size={14} /></button> : null}<strong>{stale ? "STALE" : rows.length}</strong></div></div>
      <div className="watch-tools">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search symbol" aria-label="Search symbol" />
        <select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)} aria-label="Signal filter">
          <option>ALL</option><option>LONG</option><option>SHORT</option><option>WATCH_LONG</option><option>WATCH_SHORT</option><option>WAIT</option><option>AVOID</option>
        </select>
      </div>
      <div className="watch-list">
        {rows.length === 0 ? <div className="empty">No market data</div> : null}
        {rows.map((item) => (
          <button key={item.symbol} className={`${activeSymbol === item.symbol ? "selected" : ""}${stale ? " stale-row" : ""}`} onClick={() => setActiveSymbol(item.symbol)}>
            <span>{item.coin}</span>
            <strong className={normalizeAnalysisDecision(item).className}>{normalizeAnalysisDecision(item).label}</strong>
            <em>{item.confidence}%</em>
            <small>{formatPrice(item.indicators.close)} / {item.dataProvider}{stale ? " / stale" : ""}</small>
          </button>
        ))}
      </div>
    </aside>
  );
}

type MainSeries = ISeriesApi<"Candlestick" | "Bar" | "Line" | "Area">;
type ChartSeries = ISeriesApi<"Candlestick" | "Bar" | "Line" | "Area" | "Histogram">;
type SeriesKind = "ohlc" | "close";
type ChartPriceLine = { price: number; color: string; title: string };

function sameCandle(left: Candle | undefined, right: Candle) {
  return !!left
    && left.open === right.open
    && left.high === right.high
    && left.low === right.low
    && left.close === right.close
    && left.volume === right.volume;
}

function indicatorValue(name: string, closes: number[]) {
  if (name === "ema20") return ema(closes, 20);
  if (name === "ema50") return ema(closes, 50);
  if (name === "ema200") return ema(closes, 200);
  if (name === "sma20") return sma(closes, 20);
  if (name === "sma50") return sma(closes, 50);
  return null;
}

function isDevelopmentChartMode() {
  return !!(import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV;
}

function resetMainPriceScale(chart: IChartApi | null) {
  const priceScale = chart?.priceScale("right");
  if (!priceScale) return;
  priceScale.applyOptions(mainPriceScaleOptions);
  priceScale.setAutoScale(true);
}

function clampMenuPosition(x: number, y: number) {
  const width = Math.min(320, window.innerWidth - 16);
  const height = Math.min(560, window.innerHeight - 16);
  return {
    x: Math.max(8, Math.min(x, window.innerWidth - width - 8)),
    y: Math.max(8, Math.min(y, window.innerHeight - height - 8))
  };
}

const drawingToolOptions = [
  { id: "measure" as const, label: "Messwerkzeug", Icon: Ruler },
  { id: "risk-reward" as const, label: "Risk-Reward", Icon: TrendingUp },
  { id: "trendline" as const, label: "Trendlinie", Icon: PencilLine },
  { id: "horizontal" as const, label: "Horizontale Linie", Icon: Minus },
  { id: "vertical" as const, label: "Vertikale Linie", Icon: ArrowUpRight },
  { id: "fibonacci" as const, label: "Fibonacci", Icon: LayoutTemplate },
  { id: "rectangle" as const, label: "Rechteck", Icon: RectangleHorizontal },
  { id: "brush" as const, label: "Brush", Icon: Brush },
  { id: "arrow" as const, label: "Arrow", Icon: ArrowUpRight }
];

function drawingId() {
  return `drawing-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function percent(value: number) {
  return Math.max(0, Math.min(100, value * 100));
}

function ChartDrawings({ annotations, pending, interactive, onPointerDown }: { annotations: ChartAnnotation[]; pending: { tool: ChartDrawingTool; start: { x: number; y: number } } | null; interactive: boolean; onPointerDown: React.PointerEventHandler<SVGSVGElement> }) {
  const drawing = (item: ChartAnnotation) => {
    const end = item.end || item.start;
    const x1 = percent(item.start.x);
    const y1 = percent(item.start.y);
    const x2 = percent(end.x);
    const y2 = percent(end.y);
    const minX = Math.min(x1, x2);
    const minY = Math.min(y1, y2);
    const width = Math.max(0.4, Math.abs(x2 - x1));
    const height = Math.max(0.4, Math.abs(y2 - y1));
    if (item.tool === "horizontal") return <line key={item.id} className="chart-drawing-line level" x1="0" y1={y1} x2="100" y2={y1} />;
    if (item.tool === "vertical") return <line key={item.id} className="chart-drawing-line level" x1={x1} y1="0" x2={x1} y2="100" />;
    if (item.tool === "rectangle") return <rect key={item.id} className="chart-drawing-rectangle" x={minX} y={minY} width={width} height={height} />;
    if (item.tool === "risk-reward") return <g key={item.id}><rect className="chart-drawing-risk" x={minX} y={minY} width={width} height={height} /><line className="chart-drawing-line risk" x1={minX} y1={y1} x2={minX + width} y2={y1} /></g>;
    if (item.tool === "fibonacci") return <g key={item.id}>{[0, .236, .382, .5, .618, 1].map((ratio) => <line key={ratio} className="chart-drawing-fib" x1={minX} y1={y1 + (y2 - y1) * ratio} x2={minX + width} y2={y1 + (y2 - y1) * ratio} />)}</g>;
    return <line key={item.id} className={`chart-drawing-line ${item.tool}`} x1={x1} y1={y1} x2={x2} y2={y2} markerEnd={item.tool === "arrow" ? "url(#youdinix-chart-arrow)" : undefined} />;
  };
  return <svg className={interactive ? "chart-drawings interactive" : "chart-drawings"} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" onPointerDown={onPointerDown}><defs><marker id="youdinix-chart-arrow" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto"><path d="M0,0 L5,2.5 L0,5 z" /></marker></defs>{annotations.map(drawing)}{pending ? <circle className="chart-drawing-pending" cx={percent(pending.start.x)} cy={percent(pending.start.y)} r="1.1" /> : null}</svg>;
}

function TradingChart({ symbol, signal, timeframe, setTimeframe, chartType, setChartType, activeIndicators, setActiveIndicators, autoRefresh, candles, history, updateMode, candleError, candleLoading, lastUpdate, reloadCandles }: {
  symbol: string | null;
  signal: TradingSignal | null;
  timeframe: TimeframeLabel;
  setTimeframe: (timeframe: TimeframeLabel) => void;
  chartType: ChartType;
  setChartType: (chartType: ChartType) => void;
  activeIndicators: string[];
  setActiveIndicators: (items: string[]) => void;
  autoRefresh: boolean;
  candles: Candle[];
  history?: CandleResult["history"];
  updateMode: CandleUpdateMode;
  candleError: string | null;
  candleLoading: boolean;
  lastUpdate: number | null;
  reloadCandles: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const mainSeriesRef = useRef<MainSeries | null>(null);
  const mainSeriesKindRef = useRef<SeriesKind>("ohlc");
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const indicatorSeriesRef = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  const allSeriesRef = useRef<ChartSeries[]>([]);
  const priceLinesRef = useRef<Array<ReturnType<MainSeries["createPriceLine"]>>>([]);
  const renderedCandlesRef = useRef<Map<number, Candle>>(new Map());
  const rawCandlesRef = useRef<Candle[]>([]);
  const displayedCandlesRef = useRef<Candle[]>([]);
  const chartIdentityRef = useRef("");
  const seriesLayoutRef = useRef("");
  const [ohlc, setOhlc] = useState<Candle | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [grid, setGrid] = useState(readSettings().grid ?? true);
  const [contextMenu, setContextMenu] = useState<ChartContextMenu>({ open: false, x: 0, y: 0, touch: false, kind: "chart", candle: null, price: null });
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const [crosshair, setCrosshair] = useState(readSettings().crosshair ?? true);
  const [magnet, setMagnet] = useState(readSettings().magnet ?? false);
  const [toolboxOpen, setToolboxOpen] = useState(false);
  const [drawingTool, setDrawingTool] = useState<ChartDrawingTool | null>(null);
  const [pendingDrawing, setPendingDrawing] = useState<{ tool: ChartDrawingTool; start: { x: number; y: number } } | null>(null);
  const [annotations, setAnnotations] = useState<ChartAnnotation[]>(() => normalizeChartAnnotations(readSettings().chartDrawings?.[chartDrawingStorageKey(symbol, timeframe)]));
  const [savedLayouts, setSavedLayouts] = useState<Record<string, SavedChartLayout>>(() => readSettings().chartLayouts || {});
  const [selectedLayout, setSelectedLayout] = useState("Trend");

  const safeCandles = useMemo(() => normalizeChartCandles(candles), [candles]);
  const displayedCandles = useMemo(() => chartType === "Heikin Ashi" ? heikinAshi(safeCandles) : safeCandles, [safeCandles, chartType]);
  const stats = useMemo(() => candleStats(safeCandles), [safeCandles]);
  const indicatorKey = useMemo(() => [...activeIndicators].sort().join("|"), [activeIndicators]);
  const drawingStorageKey = chartDrawingStorageKey(symbol, timeframe);
  const layoutChoices = { ...builtInChartLayouts, ...savedLayouts };

  const persistAnnotations = (next: ChartAnnotation[]) => {
    const normalized = normalizeChartAnnotations(next);
    setAnnotations(normalized);
    saveSettings({ chartDrawings: { ...(readSettings().chartDrawings || {}), [drawingStorageKey]: normalized } });
  };

  const applyLayout = (name: string) => {
    const layout = layoutChoices[name];
    if (!layout) return;
    setSelectedLayout(name);
    setChartType(layout.chartType);
    setActiveIndicators([...layout.indicators]);
    setGrid(layout.grid);
    setCrosshair(layout.crosshair);
    setMagnet(layout.magnet);
  };

  const saveCurrentLayout = () => {
    const name = window.prompt("Name des Chart-Layouts", `${symbol?.replace("USDT", "") || "Market"} Setup`)?.trim().slice(0, 36);
    if (!name) return;
    const layout: SavedChartLayout = { chartType, indicators: [...activeIndicators], grid, crosshair, magnet };
    const next = { ...savedLayouts, [name]: layout };
    setSavedLayouts(next);
    setSelectedLayout(name);
    saveSettings({ chartLayouts: next });
  };

  const clearDrawings = () => {
    setPendingDrawing(null);
    persistAnnotations([]);
  };

  const undoDrawing = () => {
    setPendingDrawing(null);
    persistAnnotations(annotations.slice(0, -1));
  };

  const toggleIndicator = (name: string) => {
    const next = activeIndicators.includes(name) ? activeIndicators.filter((item) => item !== name) : [...activeIndicators, name];
    setActiveIndicators(next);
  };

  const zoomLogicalRange = (factor: number) => {
    const timeScale = chartRef.current?.timeScale();
    const range = timeScale?.getVisibleLogicalRange();
    if (!timeScale || !range) return;
    const center = (range.from + range.to) / 2;
    const half = ((range.to - range.from) / 2) * factor;
    timeScale.setVisibleLogicalRange({ from: center - half, to: center + half });
  };

  const resetChartView = () => {
    resetMainPriceScale(chartRef.current);
    if (shouldFitChart(updateMode, false, true)) chartRef.current?.timeScale().fitContent();
  };

  const runContextAction = (action: () => void | Promise<void>) => {
    setContextMenu((menu) => ({ ...menu, open: false }));
    void action();
  };

  const copyCurrentOhlc = async () => {
    const candle = ohlc || safeCandles.at(-1);
    if (!candle) return;
    const text = [
      `${symbol || "Unknown"} ${timeframe}`,
      `Time: ${new Date(candle.openTime).toISOString()}`,
      `Open: ${formatPrice(candle.open)}`,
      `High: ${formatPrice(candle.high)}`,
      `Low: ${formatPrice(candle.low)}`,
      `Close: ${formatPrice(candle.close)}`,
      `Volume: ${formatPrice(candle.volume)}`
    ].join("\n");
    await copyText(text);
  };

  const contextFromPoint = (clientX: number, clientY: number): Pick<ChartContextMenu, "kind" | "candle" | "price"> => {
    const container = containerRef.current;
    const chart = chartRef.current;
    if (!container || !chart) return { kind: "chart", candle: null, price: null };
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const time = chart.timeScale().coordinateToTime(x);
    const price = mainSeriesRef.current?.coordinateToPrice(y) ?? null;
    const candle = time ? displayedCandlesRef.current.find((item) => Math.floor(item.openTime / 1000) === Number(time)) || null : null;
    const signalLine = signal && price !== null && [signal.entry, signal.stopLoss, signal.takeProfit].some((level) => level && Math.abs(level - price) / price < 0.002);
    if (x > rect.width - 54) return { kind: "price-axis", candle: null, price };
    if (y > rect.height - 34) return { kind: "time-axis", candle, price };
    if (signalLine) return { kind: "signal-line", candle, price };
    if (candle) return { kind: "candle", candle, price };
    return { kind: "chart", candle: null, price };
  };

  const drawingPointFromPointer = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    const chart = chartRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    let x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
    if (magnet && chart) {
      const time = chart.timeScale().coordinateToTime(x);
      if (time) {
        const nearest = displayedCandlesRef.current.reduce<Candle | null>((closest, candle) => {
          if (!closest) return candle;
          return Math.abs(candle.openTime / 1000 - Number(time)) < Math.abs(closest.openTime / 1000 - Number(time)) ? candle : closest;
        }, null);
        const snapped = nearest ? chart.timeScale().timeToCoordinate(toChartTime(nearest.openTime)) : null;
        if (snapped !== null) x = Math.max(0, Math.min(rect.width, snapped));
      }
    }
    return { x: x / rect.width, y: y / rect.height };
  };

  const placeDrawing = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!drawingTool || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const point = drawingPointFromPointer(event.clientX, event.clientY);
    if (!point) return;
    if (drawingToolNeedsEnd(drawingTool)) {
      if (!pendingDrawing || pendingDrawing.tool !== drawingTool) {
        setPendingDrawing({ tool: drawingTool, start: point });
        return;
      }
      persistAnnotations([...annotations, { id: drawingId(), tool: drawingTool, start: pendingDrawing.start, end: point, createdAt: Date.now() }]);
      setPendingDrawing(null);
      return;
    }
    persistAnnotations([...annotations, { id: drawingId(), tool: drawingTool, start: point, createdAt: Date.now() }]);
  };

  const openContextMenu = (clientX: number, clientY: number, touch = false) => {
    const position = clampMenuPosition(clientX, clientY);
    setContextMenu({ open: true, x: position.x, y: position.y, touch, ...contextFromPoint(clientX, clientY) });
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  };

  const startLongPress = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse") return;
    clearLongPressTimer();
    const { clientX, clientY } = event;
    longPressTimerRef.current = window.setTimeout(() => openContextMenu(clientX, clientY, true), 520);
  };

  const copyText = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
  };

  const removeSeries = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    priceLinesRef.current.forEach((line) => mainSeriesRef.current?.removePriceLine(line));
    priceLinesRef.current = [];
    allSeriesRef.current.forEach((series) => chart.removeSeries(series));
    allSeriesRef.current = [];
    mainSeriesRef.current = null;
    volumeSeriesRef.current = null;
    indicatorSeriesRef.current.clear();
  }, []);

  const applyPriceLines = useCallback(() => {
    const mainSeries = mainSeriesRef.current;
    if (!mainSeries) return;
    priceLinesRef.current.forEach((line) => mainSeries.removePriceLine(line));
    priceLinesRef.current = [];
    const marketPrice = rawCandlesRef.current.at(-1)?.close ?? null;
    const signalMatchesSymbol = !!signal && !!symbol && signal.symbol === symbol;
    const candidates: ChartPriceLine[] = signalMatchesSymbol ? [
      { price: signal.entry ?? NaN, color: "#38bdf8", title: "Entry" },
      { price: signal.stopLoss ?? NaN, color: "#f87171", title: "Stop" },
      { price: signal.takeProfit ?? NaN, color: "#4ade80", title: "TP1" }
    ] : [];
    for (const line of candidates) {
      if (!isPlausiblePriceLine(line.price, marketPrice)) {
        if (isDevelopmentChartMode()) {
          console.warn("[chart] skipped implausible price line", { symbol, timeframe, title: line.title, price: line.price, marketPrice });
        }
        continue;
      }
      priceLinesRef.current.push(mainSeries.createPriceLine({ price: line.price, color: line.color, lineWidth: 1, title: line.title }));
    }
  }, [signal, symbol, timeframe]);

  const updateRenderedSnapshot = useCallback((items: Candle[]) => {
    renderedCandlesRef.current = new Map(items.map((candle) => [candle.openTime, candle]));
  }, []);

  const rebuildSeries = useCallback((fitAfterBuild: boolean) => {
    const chart = chartRef.current;
    if (!chart || displayedCandlesRef.current.length === 0) return;
    removeSeries();
    resetMainPriceScale(chart);
    const currentDisplayed = displayedCandlesRef.current;
    const currentRaw = rawCandlesRef.current;
    const lastPrice = currentDisplayed.at(-1)?.close || 1;
    const priceFormat = { type: "price" as const, ...chartPricePrecision(lastPrice) };
    const up = "#4ade80";
    const down = "#f87171";
    const ohlcData = toOhlcSeries(currentDisplayed);
    const closeData = toCloseSeries(currentDisplayed);
    const diagnostics = [
      seriesDiagnostic("Candles", "right", ohlcData, symbol, timeframe, true)
    ];
    let mainSeries: MainSeries;
    let kind: SeriesKind = "ohlc";

    if (chartType === "Bars") {
      mainSeries = chart.addSeries(BarSeries, { upColor: up, downColor: down, priceLineVisible: true, lastValueVisible: true, priceFormat }, mainPricePaneIndex);
      mainSeries.setData(ohlcData);
    } else if (chartType === "Line") {
      kind = "close";
      mainSeries = chart.addSeries(LineSeries, { color: "#7dd3fc", lineWidth: 2, priceLineVisible: true, priceFormat }, mainPricePaneIndex);
      mainSeries.setData(closeData);
    } else if (chartType === "Area") {
      kind = "close";
      mainSeries = chart.addSeries(AreaSeries, { lineColor: "#7dd3fc", topColor: "rgba(56,189,248,0.28)", bottomColor: "rgba(56,189,248,0.02)", priceFormat }, mainPricePaneIndex);
      mainSeries.setData(closeData);
    } else {
      mainSeries = chart.addSeries(CandlestickSeries, { upColor: up, downColor: down, borderUpColor: up, borderDownColor: down, wickUpColor: up, wickDownColor: down, priceLineVisible: true, lastValueVisible: true, priceFormat }, mainPricePaneIndex);
      mainSeries.setData(ohlcData);
    }

    mainSeriesRef.current = mainSeries;
    mainSeriesKindRef.current = kind;
    allSeriesRef.current.push(mainSeries);

    const closes = currentRaw.map((candle) => candle.close);
    const addLine = (name: string, values: Array<number | null>, color: string) => {
      if (!activeIndicators.includes(name)) return;
      const series = chart.addSeries(LineSeries, { color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat }, mainPricePaneIndex);
      const lineData = values.map((value, index) => value === null ? null : { time: toChartTime(currentRaw[index].openTime), value }).filter(Boolean) as Array<{ time: UTCTimestamp; value: number }>;
      series.setData(lineData);
      indicatorSeriesRef.current.set(name, series);
      allSeriesRef.current.push(series);
      diagnostics.push(seriesDiagnostic(name.toUpperCase(), "right", lineData, symbol, timeframe, true));
    };
    addLine("ema20", closes.map((_, index) => ema(closes.slice(0, index + 1), 20)), "#fbbf24");
    addLine("ema50", closes.map((_, index) => ema(closes.slice(0, index + 1), 50)), "#38bdf8");
    addLine("ema200", closes.map((_, index) => ema(closes.slice(0, index + 1), 200)), "#c084fc");
    addLine("sma20", closes.map((_, index) => sma(closes.slice(0, index + 1), 20)), "#f97316");
    addLine("sma50", closes.map((_, index) => sma(closes.slice(0, index + 1), 50)), "#22d3ee");

    if (activeIndicators.includes("volume")) {
      const volumeSeries = chart.addSeries(HistogramSeries, { priceFormat: { type: "volume" }, priceScaleId: "right", color: "rgba(125,211,252,0.28)", lastValueVisible: false, priceLineVisible: false }, volumePaneIndex);
      const volumeData = toVolumeSeries(currentRaw);
      volumeSeries.setData(volumeData);
      volumeSeriesRef.current = volumeSeries;
      allSeriesRef.current.push(volumeSeries);
      diagnostics.push(seriesDiagnostic("Volume", "right:pane-1", volumeData, symbol, timeframe, false));
    }

    applyPriceLines();
    updateRenderedSnapshot(currentDisplayed);
    if (isDevelopmentChartMode()) console.table(diagnostics);
    if (fitAfterBuild) chart.timeScale().fitContent();
  }, [activeIndicators, applyPriceLines, chartType, removeSeries, symbol, timeframe, updateRenderedSnapshot]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const chart = createChart(container, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: "#0a0f15" }, textColor: "#a8b3bd" },
      grid: {
        vertLines: { color: grid ? "#202a36" : "transparent" },
        horzLines: { color: grid ? "#202a36" : "transparent" }
      },
      rightPriceScale: { borderColor: "#3a4654", ...mainPriceScaleOptions },
      timeScale: { borderColor: "#3a4654", timeVisible: true, secondsVisible: timeframe === "M1", barSpacing: 8, minBarSpacing: 4, rightOffset: 8, rightBarStaysOnScroll: true },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: chartHandleScrollOptions,
      handleScale: chartHandleScaleOptions
    });
    chartRef.current = chart;
    const resize = new ResizeObserver(([entry]) => {
      const width = Math.max(1, Math.floor(entry.contentRect.width));
      const height = Math.max(1, Math.floor(entry.contentRect.height));
      chart.resize(width, height);
    });
    resize.observe(container);
    const crosshairHandler: Parameters<IChartApi["subscribeCrosshairMove"]>[0] = (param) => {
      if (!param.time) return;
      const time = Number(param.time) * 1000;
      setOhlc(rawCandlesRef.current.find((candle) => candle.openTime === time) || null);
    };
    chart.subscribeCrosshairMove(crosshairHandler);
    return () => {
      chart.unsubscribeCrosshairMove(crosshairHandler);
      resize.disconnect();
      removeSeries();
      chart.remove();
      chartRef.current = null;
    };
  }, [removeSeries]);

  useEffect(() => {
    chartRef.current?.applyOptions({
      grid: {
        vertLines: { color: grid ? "#202a36" : "transparent" },
        horzLines: { color: grid ? "#202a36" : "transparent" }
      }
    });
    saveSettings({ grid });
  }, [grid]);

  useEffect(() => {
    chartRef.current?.applyOptions({ crosshair: { mode: crosshair ? CrosshairMode.Normal : CrosshairMode.Hidden } });
    saveSettings({ crosshair });
  }, [crosshair]);

  useEffect(() => {
    saveSettings({ magnet });
  }, [magnet]);

  useEffect(() => {
    setAnnotations(normalizeChartAnnotations(readSettings().chartDrawings?.[drawingStorageKey]));
    setPendingDrawing(null);
  }, [drawingStorageKey]);

  useEffect(() => {
    rawCandlesRef.current = safeCandles;
    displayedCandlesRef.current = displayedCandles;
  }, [displayedCandles, safeCandles]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || displayedCandles.length === 0) return;
    const identity = `${symbol || ""}:${timeframe}`;
    const layout = `${chartType}:${indicatorKey}`;
    const identityChanged = chartIdentityRef.current !== identity;
    const layoutChanged = seriesLayoutRef.current !== layout;
    const hasSeries = !!mainSeriesRef.current;
    const fitAfterBuild = shouldFitChart(updateMode, identityChanged);

    if (!hasSeries || identityChanged || layoutChanged || updateMode === "full") {
      chartIdentityRef.current = identity;
      seriesLayoutRef.current = layout;
      rebuildSeries(fitAfterBuild);
      return;
    }

    const previous = renderedCandlesRef.current;
    const changedDisplayed = displayedCandles.filter((candle) => !sameCandle(previous.get(candle.openTime), candle));
    if (changedDisplayed.length === 0) return;

    const mainSeries = mainSeriesRef.current;
    if (mainSeries) {
      for (const candle of changedDisplayed) {
        if (mainSeriesKindRef.current === "close") {
          mainSeries.update({ time: toChartTime(candle.openTime), value: candle.close } as Parameters<typeof mainSeries.update>[0]);
        } else {
          mainSeries.update({ time: toChartTime(candle.openTime), open: candle.open, high: candle.high, low: candle.low, close: candle.close } as Parameters<typeof mainSeries.update>[0]);
        }
      }
    }

    const rawByTime = new Map(safeCandles.map((candle) => [candle.openTime, candle]));
    const changedRaw = changedDisplayed.map((candle) => rawByTime.get(candle.openTime)).filter(Boolean) as Candle[];
    if (volumeSeriesRef.current) {
      for (const candle of changedRaw) {
        volumeSeriesRef.current.update(toVolumeSeries([candle])[0]);
      }
    }

    const closes = safeCandles.map((candle) => candle.close);
    for (const [name, series] of indicatorSeriesRef.current) {
      const firstChangedIndex = changedRaw.reduce((lowest, candle) => {
        const index = safeCandles.findIndex((item) => item.openTime === candle.openTime);
        return index < 0 ? lowest : Math.min(lowest, index);
      }, Number.POSITIVE_INFINITY);
      if (!Number.isFinite(firstChangedIndex)) continue;
      for (let index = firstChangedIndex; index < safeCandles.length; index += 1) {
        const candle = safeCandles[index];
        const value = indicatorValue(name, closes.slice(0, index + 1));
        if (value !== null) series.update({ time: toChartTime(candle.openTime), value });
      }
    }

    updateRenderedSnapshot(displayedCandles);
  }, [chartType, displayedCandles, indicatorKey, rebuildSeries, safeCandles, symbol, timeframe, updateMode, updateRenderedSnapshot]);

  useEffect(() => {
    applyPriceLines();
  }, [applyPriceLines]);

  useEffect(() => {
    if (!contextMenu.open) return undefined;
    window.setTimeout(() => contextMenuRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus(), 0);
    const close = () => setContextMenu((menu) => ({ ...menu, open: false }));
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && (contextMenuRef.current?.contains(target) || containerRef.current?.contains(target))) return;
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [contextMenu.open]);

  useEffect(() => {
    setContextMenu((menu) => ({ ...menu, open: false }));
    clearLongPressTimer();
  }, [symbol, timeframe, chartType]);

  useEffect(() => () => clearLongPressTimer(), []);

  const onContextMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(contextMenuRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") || []);
    const current = document.activeElement instanceof HTMLButtonElement ? buttons.indexOf(document.activeElement) : -1;
    if (event.key === "Escape") {
      setContextMenu((menu) => ({ ...menu, open: false }));
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(event.key)) return;
    event.preventDefault();
    if (event.key === "Enter" || event.key === " ") {
      if (document.activeElement instanceof HTMLButtonElement) document.activeElement.click();
      return;
    }
    const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : event.key === "ArrowUp" ? Math.max(0, current - 1) : Math.min(buttons.length - 1, current + 1);
    buttons[nextIndex]?.focus();
  };

  const screenshot = () => {
    const canvas = containerRef.current?.querySelector("canvas");
    const url = canvas?.toDataURL("image/png");
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${symbol || "chart"}-${timeframe}.png`;
    link.click();
  };

  const contextCandle = contextMenu.candle || ohlc || safeCandles.at(-1) || null;
  const contextChange = contextCandle ? contextCandle.close - contextCandle.open : null;
  const signalPrice = contextMenu.price ?? signal?.entry ?? null;

  return (
    <section className={fullscreen ? "panel chart-panel fullscreen" : "panel chart-panel"}>
      <div className="chart-toolbar">
        <div className="timeframes">
          {timeframeOptions.map((item) => <button key={item.label} className={timeframe === item.label ? "active" : ""} onClick={() => setTimeframe(item.label)}>{item.label}</button>)}
        </div>
        <select value={chartType} onChange={(event) => setChartType(event.target.value as ChartType)} aria-label="Chart type">
          {chartTypes.map((item) => <option key={item}>{item}</option>)}
        </select>
        <select value={selectedLayout} onChange={(event) => applyLayout(event.target.value)} aria-label="Chart layout">
          {Object.keys(layoutChoices).sort((left, right) => left.localeCompare(right)).map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        <button className="toolbar-button" onClick={() => setGrid(!grid)}>Grid {grid ? "ON" : "OFF"}</button>
        <button className="toolbar-button" onClick={() => zoomLogicalRange(0.72)}>Zoom In</button>
        <button className="toolbar-button" onClick={() => zoomLogicalRange(1.35)}>Zoom Out</button>
        <button className="toolbar-button" onClick={() => chartRef.current?.priceScale("right").setAutoScale(true)}>Auto Scale</button>
        <button className="toolbar-button" onClick={resetChartView}>Reset</button>
        <button className="toolbar-button" onClick={() => chartRef.current?.timeScale().scrollToRealTime()}>Go Live</button>
        <button className={toolboxOpen ? "icon-button active" : "icon-button"} onClick={() => setToolboxOpen(!toolboxOpen)} aria-label="Chartwerkzeuge" title="Chartwerkzeuge"><PencilLine size={15} /></button>
        <button className="icon-button" onClick={saveCurrentLayout} aria-label="Chart-Layout speichern" title="Chart-Layout speichern"><Save size={15} /></button>
        <button className="icon-button" onClick={screenshot} aria-label="Chart screenshot"><Camera size={15} /></button>
        <button className="icon-button" onClick={() => setFullscreen(!fullscreen)} aria-label="Fullscreen chart"><Maximize2 size={15} /></button>
        <button className="icon-button" onClick={reloadCandles} aria-label="Reload candles"><RefreshCw size={15} /></button>
      </div>
      <div className={toolboxOpen ? "chart-tools" : "chart-tools collapsed"} aria-label="MT5 Chartwerkzeuge">
        {toolboxOpen ? <><div className="chart-tool-group">{drawingToolOptions.map(({ id, label, Icon }) => <button key={id} className={drawingTool === id ? "icon-button active" : "icon-button"} type="button" onClick={() => { setDrawingTool(drawingTool === id ? null : id); setPendingDrawing(null); }} aria-label={label} title={label}><Icon size={15} /></button>)}</div>
          <div className="chart-tool-group chart-tool-actions"><button className={crosshair ? "icon-button active" : "icon-button"} type="button" onClick={() => setCrosshair(!crosshair)} aria-label="Crosshair" title="Crosshair"><Crosshair size={15} /></button><button className={magnet ? "icon-button active" : "icon-button"} type="button" onClick={() => setMagnet(!magnet)} aria-label="Magnet" title="Magnet: auf Candles einrasten"><Magnet size={15} /></button><button className="icon-button" type="button" onClick={undoDrawing} disabled={!annotations.length && !pendingDrawing} aria-label="Letzte Zeichnung entfernen" title="Letzte Zeichnung entfernen"><Undo2 size={15} /></button><button className="icon-button" type="button" onClick={clearDrawings} disabled={!annotations.length && !pendingDrawing} aria-label="Zeichnungen loeschen" title="Zeichnungen loeschen"><Eraser size={15} /></button></div>
          <span className="chart-tool-status">{drawingTool ? `${drawingToolOptions.find((item) => item.id === drawingTool)?.label}: ${pendingDrawing ? "Endpunkt waehlen" : drawingToolNeedsEnd(drawingTool) ? "Startpunkt waehlen" : "im Chart platzieren"}` : `${annotations.length} gespeicherte Zeichnung${annotations.length === 1 ? "" : "en"}`}</span></> : null}
      </div>
      <div className="indicator-strip">
        {["volume", "ema20", "ema50", "ema200", "sma20", "sma50"].map((item) => (
          <button key={item} className={activeIndicators.includes(item) ? "active" : ""} onClick={() => toggleIndicator(item)}>{item.toUpperCase()}</button>
        ))}
      </div>
      <div className="chart-title">
        <span>{symbol?.replace("USDT", "/USDT") || "No symbol"} {timeframe} {chartType}</span>
        <strong>{candleError && candles.length ? `STALE DATA ${formatTime(lastUpdate)}` : candleLoading ? "Loading" : autoRefresh ? "Live polling" : "Manual"}</strong>
      </div>
      {history?.limited ? <div className="chart-error limited"><AlertTriangle size={14} />Limited history · {history.receivedCount} candles · Begins {formatTime(history.oldestTimestamp || null)} · {history.isAggregated ? "D1 aggregated" : "Native provider history"}</div> : null}
      {candleError ? <div className="chart-error compact"><AlertTriangle size={14} />{candles.length ? `Stale data. Last error: ${compactErrorText(candleError)}` : compactErrorText(candleError)}</div> : null}
      <div
        className="chart-stage"
        onContextMenu={(event) => {
          event.preventDefault();
          openContextMenu(event.clientX, event.clientY);
        }}
        onPointerDown={startLongPress}
        onPointerMove={clearLongPressTimer}
        onPointerUp={clearLongPressTimer}
        onPointerCancel={clearLongPressTimer}
      ><div className="lightweight-chart" ref={containerRef} /><ChartDrawings annotations={annotations} pending={pendingDrawing} interactive={Boolean(drawingTool)} onPointerDown={placeDrawing} /></div>
      {contextMenu.open ? (
        <div
          ref={contextMenuRef}
          className={contextMenu.touch ? "chart-context-menu touch" : "chart-context-menu"}
          style={{ left: contextMenu.x, top: contextMenu.y }}
          role="menu"
          aria-label="Chart context menu"
          tabIndex={-1}
          onKeyDown={onContextMenuKeyDown}
        >
          <div className="chart-context-heading">
            <strong>{contextMenu.kind === "candle" ? "Candle" : contextMenu.kind === "signal-line" ? "Signal Level" : contextMenu.kind === "price-axis" ? "Price Axis" : contextMenu.kind === "time-axis" ? "Time Axis" : "Chart"}</strong>
            <span>{symbol || "No symbol"} {timeframe}</span>
          </div>
          {contextMenu.kind === "candle" && contextCandle ? (
            <div className="chart-context-section">
              <span>Candle Details</span>
              <div className="chart-context-details">
                <b>Time</b><em>{new Date(contextCandle.openTime).toISOString()}</em>
                <b>Open</b><em>{formatPrice(contextCandle.open)}</em>
                <b>High</b><em>{formatPrice(contextCandle.high)}</em>
                <b>Low</b><em>{formatPrice(contextCandle.low)}</em>
                <b>Close</b><em>{formatPrice(contextCandle.close)}</em>
                <b>Change</b><em>{formatPrice(contextChange)}</em>
                <b>Volume</b><em>{formatPrice(contextCandle.volume)}</em>
              </div>
              <button role="menuitem" onClick={() => runContextAction(copyCurrentOhlc)}>Copy OHLC</button>
              <button role="menuitem" onClick={() => runContextAction(() => copyText(new Date(contextCandle.openTime).toISOString()))}>Copy Time</button>
              <button role="menuitem" onClick={() => runContextAction(() => copyText(String(contextCandle.close)))}>Copy Close Price</button>
            </div>
          ) : null}
          {contextMenu.kind === "signal-line" && signal ? (
            <div className="chart-context-section">
              <span>Signal Level</span>
              <div className="chart-context-details">
                <b>Signal</b><em>{signal.id}</em>
                <b>Direction</b><em>{normalizeAnalysisDecision(signal).label}</em>
                <b>Entry</b><em>{formatPrice(signal.entry)}</em>
                <b>Stop</b><em>{formatPrice(signal.stopLoss)}</em>
                <b>TP1</b><em>{formatPrice(signal.takeProfit)}</em>
              </div>
              <button role="menuitem" onClick={() => runContextAction(() => copyText(String(signalPrice || "")))}>Copy Price</button>
              <button role="menuitem" onClick={() => runContextAction(resetChartView)}>Focus Signal</button>
            </div>
          ) : null}
          <div className="chart-context-section">
            <span>Chart Type</span>
            {chartTypes.map((item) => (
              <button key={item} role="menuitemradio" aria-checked={chartType === item} className={chartType === item ? "active" : ""} onClick={() => runContextAction(() => setChartType(item))}>{item}</button>
            ))}
          </div>
          <div className="chart-context-section">
            <span>Timeframe</span>
            <div className="chart-context-grid">
              {timeframeOptions.map((item) => (
                <button key={item.label} role="menuitemradio" aria-checked={timeframe === item.label} className={timeframe === item.label ? "active" : ""} onClick={() => runContextAction(() => setTimeframe(item.label))}>{item.label}</button>
              ))}
            </div>
          </div>
          <div className="chart-context-section">
            <span>Indicators</span>
            {["volume", "ema20", "ema50", "ema200", "sma20", "sma50"].map((item) => (
              <button key={item} role="menuitemcheckbox" aria-checked={activeIndicators.includes(item)} className={activeIndicators.includes(item) ? "active" : ""} onClick={() => runContextAction(() => toggleIndicator(item))}>{item.toUpperCase()}</button>
            ))}
          </div>
          <div className="chart-context-section">
            <span>View</span>
            <button role="menuitem" onClick={() => runContextAction(() => zoomLogicalRange(0.72))}>Zoom In</button>
            <button role="menuitem" onClick={() => runContextAction(() => zoomLogicalRange(1.35))}>Zoom Out</button>
            <button role="menuitem" onClick={() => runContextAction(() => chartRef.current?.priceScale("right").setAutoScale(true))}>Auto Scale</button>
            <button role="menuitem" onClick={() => runContextAction(resetChartView)}>Fit Content</button>
            <button role="menuitem" onClick={() => runContextAction(() => chartRef.current?.timeScale().scrollToRealTime())}>Go Live</button>
            <button role="menuitemcheckbox" aria-checked={grid} className={grid ? "active" : ""} onClick={() => runContextAction(() => setGrid(!grid))}>Grid {grid ? "On" : "Off"}</button>
            <button role="menuitemcheckbox" aria-checked={crosshair} className={crosshair ? "active" : ""} onClick={() => runContextAction(() => setCrosshair(!crosshair))}>Crosshair {crosshair ? "On" : "Off"}</button>
            <button role="menuitemcheckbox" aria-checked={fullscreen} className={fullscreen ? "active" : ""} onClick={() => runContextAction(() => setFullscreen(!fullscreen))}>Fullscreen {fullscreen ? "On" : "Off"}</button>
          </div>
          <div className="chart-context-section">
            <span>Tools</span>
            {contextMenu.kind !== "candle" ? <button role="menuitem" onClick={() => runContextAction(copyCurrentOhlc)} disabled={!safeCandles.length}>Copy Latest OHLC</button> : null}
            {contextMenu.price !== null ? <button role="menuitem" onClick={() => runContextAction(() => copyText(String(contextMenu.price)))}>Copy Current Price</button> : null}
            <button role="menuitem" onClick={() => runContextAction(screenshot)}>Save Screenshot</button>
            <button role="menuitem" onClick={() => runContextAction(reloadCandles)}>Reload Candles</button>
          </div>
        </div>
      ) : null}
      <div className="ohlc-row">
        <span>O {formatPrice((ohlc || stats.last)?.open)}</span>
        <span>H {formatPrice((ohlc || stats.last)?.high)}</span>
        <span>L {formatPrice((ohlc || stats.last)?.low)}</span>
        <span>C {formatPrice((ohlc || stats.last)?.close)}</span>
        <span>24H H {formatPrice(stats.high24)}</span>
        <span>24H L {formatPrice(stats.low24)}</span>
        <span>24H Vol {formatPrice(stats.volume24)}</span>
      </div>
    </section>
  );
}

function SignalPanel({ signal, scan, candles, timeframe }: { signal: TradingSignal | null; scan: MarketScan | null; candles: Candle[]; timeframe: TimeframeLabel }) {
  const decision = normalizeAnalysisDecision(signal);
  const indicators = useMemo(() => candles.length ? analyzeIndicators(candles) : signal?.indicators || null, [candles, signal]);
  const liveScore = signal?.liveScore;
  const explainer = signal?.explainer;
  const explanation = signal?.direction === "LONG" ? explainer?.whyLong : signal?.direction === "SHORT" ? explainer?.whyShort : explainer?.whyWait;
  const current = indicators?.close ?? signal?.indicators.close ?? null;
  const risk = signal?.entry && signal.stopLoss ? Math.abs(signal.entry - signal.stopLoss) : null;
  const reward = signal?.entry && signal.takeProfit ? Math.abs(signal.takeProfit - signal.entry) : null;
  const rr = risk && reward ? `1:${(reward / risk).toFixed(2)}` : "-";
  const stale = signal ? Date.now() - signal.latestClosedCandleTime > 4 * 60 * 60 * 1000 : false;
  return (
    <aside className="panel signal-panel">
      <div className="panel-header"><span>Signal Desk</span><strong className={decision.className}>{decision.label}</strong></div>
      <dl className="signal-facts">
        <div><dt>Symbol</dt><dd>{signal?.coin || "-"}</dd></div>
        <div><dt>Timeframe</dt><dd>{timeframe}</dd></div>
        <div><dt>Entry</dt><dd>{formatPrice(signal?.entry)}</dd></div>
        <div><dt>Market</dt><dd>{formatPrice(current)}</dd></div>
        <div><dt>Stop</dt><dd>{formatPrice(signal?.stopLoss)}</dd></div>
        <div><dt>TP1</dt><dd>{formatPrice(signal?.takeProfit)}</dd></div>
        <div><dt>R:R</dt><dd>{rr}</dd></div>
        <div><dt>Confidence</dt><dd>{signal ? `${signal.confidence}%` : "-"}</dd></div>
        <div><dt>Quality</dt><dd>{decision.qualified ? "Qualified" : decision.finalDirection.startsWith("WATCH") ? "Watch" : "No setup"}</dd></div>
        <div><dt>Provider</dt><dd>{signal?.dataProvider || "-"}</dd></div>
      </dl>
      {stale ? <div className="chart-error">Warning: latest candle is stale.</div> : null}
      <div className="reason-box">{signal?.shortReason || "No qualified setup"}</div>
      <div className="analysis-grid">
        <span>Bullish</span><strong>{indicators?.trend === "bullish" ? "Trend, EMA alignment" : "-"}</strong>
        <span>Bearish</span><strong>{indicators?.trend === "bearish" ? "Trend pressure" : "-"}</strong>
        <span>Neutral</span><strong>{indicators?.trend === "sideways" ? "Range / wait" : "-"}</strong>
        <span>Contradictory</span><strong>{indicators?.rsi14 && indicators.rsi14 > 70 ? "RSI overheated" : indicators?.rsi14 && indicators.rsi14 < 30 ? "RSI oversold" : "-"}</strong>
      </div>
      {liveScore ? <section className="live-score-panel"><div><span>Live Score</span><strong>{liveScore.overall}/100</strong></div><div className="live-score-grid"><span>Trend<b>{liveScore.trend}%</b></span><span>Momentum<b>{liveScore.momentum}%</b></span><span>Liquidity<b>{liveScore.liquidity}%</b></span><span>Volume<b>{liveScore.volume}%</b></span><span>Volatility<b>{liveScore.volatility}%</b></span><span>Institutional Bias<b>{liveScore.institutionalBias}%</b></span></div></section> : null}
      {explainer ? <section className="mini-section signal-explainer"><span>{signal?.direction === "LONG" ? "Warum Long?" : signal?.direction === "SHORT" ? "Warum Short?" : "Warum Wait?"}</span><strong>{explanation?.slice(0, 3).join(" ") || explainer.decisionReason}</strong><small>{explainer.comparison.slice(0, 4).map((item) => `${item.strategy}: ${item.direction}`).join(" | ")}</small></section> : null}
      <div className="mini-section">
        <span>Sentiment</span>
        <strong>{scan?.sentimentStatus.label || "Unavailable"}</strong>
        <small>{scan?.sentimentStatus.factors.join(" ") || "No sentiment data."}</small>
      </div>
    </aside>
  );
}

function BottomTerminal({ scan, error }: { scan: MarketScan | null; error: string | null }) {
  const rows = scan?.opportunities || [];
  const activeRows = rows.filter((item) => normalizeAnalysisDecision(item).qualified);
  const watchRows = rows.filter((item) => normalizeAnalysisDecision(item).finalDirection.startsWith("WATCH"));
  const neutralRows = rows.filter((item) => !normalizeAnalysisDecision(item).qualified && !normalizeAnalysisDecision(item).finalDirection.startsWith("WATCH"));
  const [tab, setTab] = useState<"active" | "watch" | "neutral" | "analysis" | "logs" | "errors">("active");
  const csv = () => {
    const header = "time,symbol,direction,entry,stop,tp,confidence,risk,provider\n";
    const body = rows.map((item) => [item.dataTimestamp, item.coin, item.direction, item.entry, item.stopLoss, item.takeProfit, item.confidence, item.riskLevel, item.dataProvider].join(",")).join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([header + body], { type: "text/csv" }));
    link.download = "signal-history.csv";
    link.click();
  };
  return (
    <section className="terminal-bottom">
      <div className="tabs" role="tablist" aria-label="Terminal tabs">
        <button role="tab" aria-selected={tab === "active"} className={tab === "active" ? "active" : ""} onClick={() => setTab("active")}>Active Signals</button>
        <button role="tab" aria-selected={tab === "watch"} className={tab === "watch" ? "active" : ""} onClick={() => setTab("watch")}>Watchlist Setups</button>
        <button role="tab" aria-selected={tab === "neutral"} className={tab === "neutral" ? "active" : ""} onClick={() => setTab("neutral")}>No Setup</button>
        <button role="tab" aria-selected={tab === "analysis"} className={tab === "analysis" ? "active" : ""} onClick={() => setTab("analysis")}>Market Analysis</button>
        <button role="tab" aria-selected={tab === "logs"} className={tab === "logs" ? "active" : ""} onClick={() => setTab("logs")}>Logs</button>
        <button role="tab" aria-selected={tab === "errors"} className={tab === "errors" ? "active" : ""} onClick={() => setTab("errors")}>Errors</button>
        <button onClick={csv}>Export CSV</button>
      </div>
      <div className="table-shell">
        {["active", "watch", "neutral"].includes(tab) ? <table>
          <thead><tr><th>Time</th><th>Symbol</th><th>Direction</th><th>Entry</th><th>Stop</th><th>TP</th><th>Confidence</th><th>Risk</th><th>Status</th><th>Provider</th></tr></thead>
          <tbody>
            {(tab === "active" ? activeRows : tab === "watch" ? watchRows : neutralRows).length === 0 ? <tr><td colSpan={10}>{error || "No rows for this view"}</td></tr> : null}
            {(tab === "active" ? activeRows : tab === "watch" ? watchRows : neutralRows).map((item) => (
              <tr key={item.symbol}>
                <td>{formatTime(item.dataTimestamp)}</td><td>{item.coin}</td><td><span className={`tag ${normalizeAnalysisDecision(item).className}`}>{normalizeAnalysisDecision(item).label}</span></td>
                <td>{formatPrice(item.entry)}</td><td>{formatPrice(item.stopLoss)}</td><td>{formatPrice(item.takeProfit)}</td><td>{item.confidence}%</td><td>{item.riskLevel}</td>
                <td>{normalizeAnalysisDecision(item).qualified ? "Qualified" : normalizeAnalysisDecision(item).label}</td><td>{item.dataProvider}</td>
              </tr>
            ))}
          </tbody>
        </table> : null}
        {tab === "analysis" ? <div className="terminal-pane">{scan?.summary || "No market analysis available."}</div> : null}
        {tab === "logs" ? <div className="terminal-pane">Last scan: {scan?.generatedAt || "pending"} | Rows: {rows.length} | Providers: {scan ? Object.entries(scan.providerSummary).map(([name, count]) => `${name}:${count}`).join(", ") : "-"}</div> : null}
        {tab === "errors" ? <div className="terminal-pane">{error || "No active errors."}</div> : null}
      </div>
    </section>
  );
}

function StatusBar({ state, scan, status }: { state: LoadState; scan: MarketScan | null; status: ApiStatus | null }) {
  return (
    <footer className="status-bar">
      <span><StatusDot state={state} />Connection {state === "offline" ? "Offline" : "Online"}</span>
      <span>API {state === "error" ? "Degraded" : "Healthy"}</span>
      <span>Data Provider {scan?.highestProbabilityOpportunity?.dataProvider || "-"}</span>
      <span>Discord {status ? "configured server-side" : "-"}</span>
      <span>Storage memory/KV-ready</span>
      <span>UTC {formatTime(Date.now())}</span>
      <span>v2.1.0</span>
    </footer>
  );
}

function App() {
  const settings = typeof window !== "undefined" ? readSettings() : {};
  const [autoRefresh, setAutoRefreshState] = useState(settings.autoRefresh ?? true);
  const [activeSymbol, setActiveSymbolState] = useState<string | null>(settings.symbol || null);
  const [timeframe, setTimeframeState] = useState<TimeframeLabel>(settings.timeframe || "H1");
  const [chartType, setChartTypeState] = useState<ChartType>(settings.chartType || "Candles");
  const [activeIndicators, setActiveIndicatorsState] = useState<string[]>(settings.indicators || defaultIndicators);
  const [mobileView, setMobileView] = useState<"watch" | "chart" | "signal" | "terminal" | "system">("chart");
  const desk = useMarketDesk(autoRefresh);
  const opportunities = desk.scan?.opportunities || [];
  const selected = useMemo(() => opportunities.find((item) => item.symbol === activeSymbol) || desk.scan?.highestProbabilityOpportunity || null, [activeSymbol, opportunities, desk.scan]);
  const candles = useCandles(selected?.symbol || activeSymbol, timeframe, autoRefresh);

  const setAutoRefresh = (value: boolean) => { setAutoRefreshState(value); saveSettings({ autoRefresh: value }); };
  const setActiveSymbol = (value: string) => { setActiveSymbolState(value); setMobileView("chart"); saveSettings({ symbol: value }); };
  const setTimeframe = (value: TimeframeLabel) => { setTimeframeState(value); saveSettings({ timeframe: value }); };
  const setChartType = (value: ChartType) => { setChartTypeState(value); saveSettings({ chartType: value }); };
  const setActiveIndicators = (value: string[]) => { setActiveIndicatorsState(value); saveSettings({ indicators: value }); };

  useEffect(() => {
    if (!activeSymbol && desk.scan?.highestProbabilityOpportunity?.symbol) setActiveSymbol(desk.scan.highestProbabilityOpportunity.symbol);
  }, [activeSymbol, desk.scan]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "SELECT" || target?.tagName === "TEXTAREA") return;
      const index = Number(event.key) - 1;
      if (index >= 0 && timeframeOptions[index]) setTimeframe(timeframeOptions[index].label);
      if (event.key.toLowerCase() === "g") saveSettings({ grid: !(readSettings().grid ?? true) });
      if (event.key.toLowerCase() === "r") candles.reload();
      if (event.key.toLowerCase() === "i") setActiveIndicators(activeIndicators.length ? [] : defaultIndicators);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeIndicators, candles, setTimeframe]);

  return (
    <div className={`terminal-app mobile-view-${mobileView}`}>
      <TopBar scan={desk.scan} status={desk.status} state={desk.state} loading={desk.loading || candles.loading} autoRefresh={autoRefresh} setAutoRefresh={setAutoRefresh} lastUpdate={candles.lastUpdate || desk.lastSuccess} nextUpdate={candles.nextUpdate || desk.nextUpdate} onRefresh={() => { void desk.reload(); candles.reload(); }} />
      <ErrorBanner status={desk.status} error={desk.error || candles.error} onRetry={() => { void desk.reload(); candles.reload(); }} />
      <main className="workspace">
        <div id="watch"><MarketWatch opportunities={opportunities} activeSymbol={selected?.symbol || activeSymbol} setActiveSymbol={setActiveSymbol} stale={desk.stale} /></div>
        <div id="chart"><TradingChart symbol={selected?.symbol || activeSymbol} signal={selected} timeframe={timeframe} setTimeframe={setTimeframe} chartType={chartType} setChartType={setChartType} activeIndicators={activeIndicators} setActiveIndicators={setActiveIndicators} autoRefresh={autoRefresh} candles={candles.result?.candles || []} history={candles.result?.history} updateMode={candles.updateMode} candleError={candles.error} candleLoading={candles.loading} lastUpdate={candles.lastUpdate} reloadCandles={candles.reload} /></div>
        <div id="signal"><SignalPanel signal={selected} scan={desk.scan} candles={candles.result?.candles || []} timeframe={timeframe} /></div>
      </main>
      <div id="terminal"><BottomTerminal scan={desk.scan} error={desk.error || candles.error} /></div>
      <div id="system"><StatusBar state={desk.state} scan={desk.scan} status={desk.status} /></div>
      {desk.state === "loading" ? <div className="loading-cover"><Activity size={22} />Loading live market data</div> : null}
      {desk.state === "offline" ? <div className="loading-cover"><WifiOff size={22} />Offline</div> : null}
      <nav className="mobile-nav" aria-label="Mobile terminal navigation">
        {[
          ["watch", "Watchlist"],
          ["chart", "Chart"],
          ["signal", "Signal"],
          ["terminal", "History"],
          ["system", "System"]
        ].map(([view, label]) => <button key={view} className={mobileView === view ? "active" : ""} onClick={() => setMobileView(view as typeof mobileView)}>{label}</button>)}
      </nav>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
